import { useLayoutEffect, useRef, useState, type RefObject } from 'react'

/**
 * Measure a tab-group strip's content width so CSS `--tab-group-strip-w`
 * can replace the fixed 48rem cap (group border must span all tabs).
 */
export function useTabGroupStripWidth(
  deps: unknown[] = [],
): [RefObject<HTMLDivElement | null>, number | null] {
  const ref = useRef<HTMLDivElement | null>(null)
  const [widthPx, setWidthPx] = useState<number | null>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const measure = () => {
      // Clone so we can read the natural width without touching the live
      // strip. Setting max-width on the live node cancels the expand
      // transition and shoves the initials badge left over the neighbor.
      const clone = el.cloneNode(true) as HTMLDivElement
      clone.style.maxWidth = 'none'
      clone.style.width = 'max-content'
      clone.style.position = 'absolute'
      clone.style.left = '0'
      clone.style.top = '0'
      clone.style.visibility = 'hidden'
      clone.style.pointerEvents = 'none'
      clone.style.transition = 'none'
      clone.style.overflow = 'visible'
      clone.style.opacity = '0'
      const parent = el.parentElement
      if (!parent) return
      parent.appendChild(clone)
      let next = 0
      try {
        next = Math.ceil(clone.scrollWidth)
      } finally {
        clone.remove()
      }
      setWidthPx((prevW) => (prevW === next ? prevW : next))
    }

    measure()
    const ro = new ResizeObserver(measure)
    for (const child of el.querySelectorAll('.tab-group__tab-wrap, .tab-group__tab')) {
      ro.observe(child)
    }
    return () => ro.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies dependency list
  }, deps)

  return [ref, widthPx]
}
